import { useCallback, useEffect, useRef, useState, type ElementType, type PointerEvent, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface SwipeAction {
	id?: string;
	label: string;
	icon?: ElementType;
	/** Background colour classes, e.g. 'bg-red-600 text-white'. */
	className?: string;
	onAction: () => void;
}

interface SwipeableRowProps {
	children: ReactNode;
	/** Revealed by swiping right (shown on the left), e.g. Read / Unread. */
	leadingActions?: SwipeAction[];
	/** Revealed by swiping left (shown on the right), e.g. Close, Delete. */
	trailingActions?: SwipeAction[];
	/** A long swipe past most of the row runs the outermost action, like iOS Mail. */
	fullSwipe?: boolean;
	disabled?: boolean;
	className?: string;
}

const ACTION_WIDTH = 76;
// iOS reserves the left screen edge for swipe-back.
const EDGE_GUARD = 24;

let closeOpenRow: (() => void) | null = null;

/**
 * A list row with iOS-style swipe actions on touch screens. Only one row is
 * open at a time; tapping anywhere closes it. Mouse input is ignored.
 *
 * Swipe actions are shortcuts: the same actions must also be available from
 * a visible button or the row's "…" sheet, for VoiceOver and keyboard users.
 */
export function SwipeableRow({
	children,
	leadingActions = [],
	trailingActions = [],
	fullSwipe = false,
	disabled = false,
	className,
}: SwipeableRowProps) {
	const rowRef = useRef<HTMLDivElement>(null);
	const contentRef = useRef<HTMLDivElement>(null);
	const [offset, setOffset] = useState(0);
	const [dragging, setDragging] = useState(false);
	const gesture = useRef<{ x: number; y: number; base: number; t: number; active: boolean } | null>(null);
	const openOffset = useRef(0);
	const leadWidth = leadingActions.length * ACTION_WIDTH;
	const trailWidth = trailingActions.length * ACTION_WIDTH;

	const settle = useCallback((next: number) => {
		openOffset.current = next;
		setOffset(next);
		if (next === 0) {
			if (closeOpenRow === close) closeOpenRow = null;
		} else {
			if (closeOpenRow && closeOpenRow !== close) closeOpenRow();
			closeOpenRow = close;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const close = useCallback(() => settle(0), [settle]);

	useEffect(() => () => {
		if (closeOpenRow === close) closeOpenRow = null;
	}, [close]);

	// While open, the next touch anywhere else closes the row instead of acting.
	useEffect(() => {
		if (offset === 0 || dragging) return;
		const onDown = (event: globalThis.PointerEvent) => {
			if (rowRef.current?.contains(event.target as Node)) return;
			close();
		};
		document.addEventListener('pointerdown', onDown, true);
		return () => document.removeEventListener('pointerdown', onDown, true);
	}, [offset, dragging, close]);

	const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
		if (disabled || event.pointerType === 'mouse' || !event.isPrimary) return;
		if (event.clientX < EDGE_GUARD && openOffset.current === 0) return;
		gesture.current = { x: event.clientX, y: event.clientY, base: openOffset.current, t: event.timeStamp, active: false };
	};

	const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
		const g = gesture.current;
		if (!g) return;
		const dx = event.clientX - g.x;
		const dy = event.clientY - g.y;
		if (!g.active) {
			if (Math.abs(dy) > 8 && Math.abs(dy) > Math.abs(dx)) {
				gesture.current = null;
				return;
			}
			if (Math.abs(dx) < 8) return;
			g.active = true;
			setDragging(true);
			event.currentTarget.setPointerCapture?.(event.pointerId);
		}
		let next = g.base + dx;
		const max = leadWidth ? (fullSwipe ? Infinity : leadWidth + 24) : 0;
		const min = trailWidth ? (fullSwipe ? -Infinity : -trailWidth - 24) : 0;
		next = Math.max(min, Math.min(max, next));
		setOffset(next);
	};

	const onPointerEnd = (event: PointerEvent<HTMLDivElement>) => {
		const g = gesture.current;
		gesture.current = null;
		if (!g?.active) return;
		setDragging(false);
		const dx = event.clientX - g.x;
		const next = g.base + dx;
		const velocity = dx / Math.max(1, event.timeStamp - g.t);
		const width = rowRef.current?.offsetWidth ?? 390;
		if (fullSwipe && trailingActions.length && next < -width * 0.6) {
			settle(0);
			trailingActions[trailingActions.length - 1].onAction();
			return;
		}
		if (fullSwipe && leadingActions.length && next > width * 0.6) {
			settle(0);
			leadingActions[0].onAction();
			return;
		}
		if (next < 0 && trailWidth && (next < -trailWidth / 2 || velocity < -0.5)) settle(-trailWidth);
		else if (next > 0 && leadWidth && (next > leadWidth / 2 || velocity > 0.5)) settle(leadWidth);
		else settle(0);
	};

	const run = (action: SwipeAction) => {
		// The action buttons go away as the row closes. Keep focus in the row, so
		// a dialog the action opens (Delete → confirm) hands it back here.
		contentRef.current?.querySelector<HTMLElement>('button, a[href], [tabindex]:not([tabindex="-1"])')?.focus({ preventScroll: true });
		settle(0);
		action.onAction();
	};

	const renderActions = (actions: SwipeAction[], side: 'leading' | 'trailing') => (
		<div
			aria-hidden={offset === 0}
			className={cn('absolute inset-y-0 flex', side === 'leading' ? 'left-0' : 'right-0')}
			style={{ width: Math.abs(offset) }}
		>
			{actions.map((action, index) => {
				const Icon = action.icon;
				return (
					<button
						key={action.id ?? action.label}
						type="button"
						tabIndex={offset === 0 ? -1 : 0}
						onClick={() => run(action)}
						className={cn(
							'flex h-full flex-1 flex-col items-center justify-center gap-1 overflow-hidden text-[13px] font-medium text-white',
							action.className ?? (index === actions.length - 1 && side === 'trailing' ? 'bg-red-600' : 'bg-zinc-500'),
						)}
					>
						{Icon && <Icon aria-hidden className="size-[22px] shrink-0" />}
						<span className="truncate px-1">{action.label}</span>
					</button>
				);
			})}
		</div>
	);

	return (
		<div ref={rowRef} className={cn('relative overflow-hidden', className)}>
			{offset > 0 && renderActions(leadingActions, 'leading')}
			{offset < 0 && renderActions(trailingActions, 'trailing')}
			<div
				ref={contentRef}
				onPointerDown={onPointerDown}
				onPointerMove={onPointerMove}
				onPointerUp={onPointerEnd}
				onPointerCancel={() => {
					const g = gesture.current;
					gesture.current = null;
					if (!g?.active) return;
					setDragging(false);
					settle(g.base);
				}}
				onClickCapture={(event) => {
					// A tap on an open row closes it instead of opening the row.
					if (openOffset.current !== 0 || dragging) {
						event.preventDefault();
						event.stopPropagation();
						close();
					}
				}}
				className={cn('relative bg-background [touch-action:pan-y]', !dragging && 'transition-transform duration-300 ease-out')}
				style={{ transform: offset ? `translate3d(${offset}px,0,0)` : undefined }}
			>
				{children}
			</div>
		</div>
	);
}
